import React, { useEffect, useState } from "react";
import { Loader2, BarChart3, PieChart as PieIcon, Clock } from "lucide-react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";
import api from "../../services/api";

const colors = ["#ef4444", "#f97316", "#f59e0b", "#06b6d4", "#22c55e"];

const severityColor = {
  critical: "bg-red-500/10 text-red-400 border-red-500/30",
  high: "bg-orange-500/10 text-orange-400 border-orange-500/30",
  medium: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  low: "bg-sky-500/10 text-sky-400 border-sky-500/30",
};

const tooltipStyle = {
  backgroundColor: "#0C0E12",
  border: "1px solid rgba(6,182,212,0.3)",
  borderRadius: "8px",
  fontSize: "12px",
  fontFamily: "monospace",
};

const BreachBreakdown = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.getDarkWebBreakdown();
        setData(r.breakdown);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading)
    return (
      <div className="min-h-screen bg-[#0C0E12] p-20 flex justify-center">
        <Loader2 className="animate-spin text-cyan-400" size={40} />
      </div>
    );
  if (!data) return null;

  const dataClassEntries = Object.entries(data.dataClassCounts || {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10)
    .map(([name, value]) => ({ name, value }));

  return (
    <div
      className="min-h-screen bg-[#0C0E12] text-gray-300 p-8"
      style={{
        backgroundImage:
          "radial-gradient(circle at 10% 0%, rgba(6,182,212,0.06), transparent 40%), radial-gradient(circle at 90% 100%, rgba(239,68,68,0.05), transparent 40%)",
      }}
    >
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-2 mb-2">
          <BarChart3 size={14} className="text-cyan-400" />
          <span className="text-[10px] tracking-[0.3em] uppercase text-cyan-400 font-mono">
            Analytics Layer
          </span>
        </div>
        <h1 className="text-3xl font-bold text-white mb-1">
          Breach <span className="text-cyan-400">Breakdown</span>
        </h1>
        <p className="text-gray-500 text-sm mb-6">
          Detailed forensic analysis of your exposure footprint.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-[#12151C] rounded-xl p-6 border border-white/5 shadow-[0_0_30px_rgba(0,0,0,0.5)]">
            <div className="flex items-center gap-2 mb-1">
              <PieIcon size={12} className="text-cyan-400" />
              <span className="text-[10px] tracking-[0.2em] uppercase text-cyan-400 font-mono">
                // Severity Distribution
              </span>
            </div>
            <h3 className="font-semibold mb-4 text-white">By Severity</h3>
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie
                  data={data.bySeverity.map((s) => ({
                    name: s.severity,
                    value: parseInt(s.count),
                  }))}
                  dataKey="value"
                  outerRadius={80}
                  label={{
                    fill: "#e5e7eb",
                    fontSize: 11,
                    fontFamily: "monospace",
                  }}
                  stroke="#0C0E12"
                  strokeWidth={2}
                >
                  {data.bySeverity.map((_, i) => (
                    <Cell key={i} fill={colors[i % colors.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} />
                <Legend
                  wrapperStyle={{
                    fontFamily: "monospace",
                    fontSize: "11px",
                    color: "#94a3b8",
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-[#12151C] rounded-xl p-6 border border-white/5 shadow-[0_0_30px_rgba(0,0,0,0.5)]">
            <div className="flex items-center gap-2 mb-1">
              <BarChart3 size={12} className="text-violet-400" />
              <span className="text-[10px] tracking-[0.2em] uppercase text-violet-400 font-mono">
                // Data Class Frequency
              </span>
            </div>
            <h3 className="font-semibold mb-4 text-white">
              Top Exposed Data Types
            </h3>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={dataClassEntries} layout="vertical">
                <defs>
                  <linearGradient id="barGrad" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#06b6d4" />
                    <stop offset="100%" stopColor="#8b5cf6" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                <XAxis
                  type="number"
                  tick={{
                    fontSize: 10,
                    fill: "#64748b",
                    fontFamily: "monospace",
                  }}
                  stroke="#374151"
                />
                <YAxis
                  dataKey="name"
                  type="category"
                  width={120}
                  tick={{
                    fontSize: 11,
                    fill: "#94a3b8",
                    fontFamily: "monospace",
                  }}
                  stroke="#374151"
                />
                <Tooltip
                  contentStyle={tooltipStyle}
                  cursor={{ fill: "rgba(6,182,212,0.05)" }}
                />
                <Bar
                  dataKey="value"
                  fill="url(#barGrad)"
                  radius={[0, 4, 4, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-[#12151C] rounded-xl p-6 border border-white/5 shadow-[0_0_30px_rgba(0,0,0,0.5)] mt-6">
          <div className="flex items-center gap-2 mb-1">
            <Clock size={12} className="text-amber-400" />
            <span className="text-[10px] tracking-[0.2em] uppercase text-amber-400 font-mono">
              // Chronological Feed
            </span>
          </div>
          <h3 className="font-semibold mb-4 text-white">
            Most Recent Exposures
          </h3>
          <div className="space-y-2">
            {(data.topBreaches || []).length === 0 ? (
              <div className="text-gray-500 font-mono text-sm py-4">
                // No recent exposures on record.
              </div>
            ) : (
              (data.topBreaches || []).map((e) => (
                <div
                  key={e.id}
                  className="flex items-center justify-between bg-black/30 border border-white/5 hover:border-cyan-500/20 rounded-lg px-4 py-3 transition-colors"
                >
                  <div>
                    <div className="font-medium text-white">
                      {e.breach?.title}
                    </div>
                    <div className="text-[10px] text-gray-500 font-mono tracking-wider mt-0.5">
                      {new Date(e.firstSeenAt).toLocaleString()}
                    </div>
                  </div>
                  <span
                    className={`text-[10px] px-2.5 py-1 rounded-md border font-mono uppercase tracking-wider ${severityColor[e.severity] || "bg-gray-500/10 text-gray-400 border-gray-500/30"}`}
                  >
                    {e.severity}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
export default BreachBreakdown;
